import { api } from '@/api';
import { ActionContext } from 'vuex';
import { IContractCreate, IContractUpdate } from '@/interfaces';
import { State } from '../state';
import { ContractState } from './state';
import { getStoreAccessors } from 'typesafe-vuex';
import { commitSetContracts, commitSetContract } from './mutations';
import { dispatchCheckApiError } from '../main/actions';
import { commitAddNotification, commitRemoveNotification } from '../main/mutations';

type MainContext = ActionContext<ContractState, State>;

export const actions = {
    async actionGetContracts(context: MainContext) {
        try {
            const response = await api.getContracts(context.rootState.main.token);
            if (response) {
                commitSetContracts(context, response.data);
            }
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionCreateContract(context: MainContext, payload: IContractCreate) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.createContract(context.rootState.main.token, payload),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetContract(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'Contract successfully created', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionUpdateContract(context: MainContext, payload: { key1: string, contract: IContractUpdate }) {
        try {
            const loadingNotification = { content: 'saving', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.updateContract(context.rootState.main.token, payload.key1, payload.contract),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetContract(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'Contract successfully updated', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
    async actionInviteContract(context: MainContext, key1: string) {
        try {
            const loadingNotification = { content: 'sending', showProgress: true };
            commitAddNotification(context, loadingNotification);
            const response = (await Promise.all([
                api.inviteContract(context.rootState.main.token, key1),
                await new Promise((resolve, reject) => setTimeout(() => resolve(), 500)),
            ]))[0];
            commitSetContract(context, response.data);
            commitRemoveNotification(context, loadingNotification);
            commitAddNotification(context, { content: 'Invite successfully sent', color: 'success' });
        } catch (error) {
            await dispatchCheckApiError(context, error);
        }
    },
};

const { dispatch } = getStoreAccessors<ContractState, State>('');

export const dispatchCreateContract = dispatch(actions.actionCreateContract);
export const dispatchGetContracts = dispatch(actions.actionGetContracts);
export const dispatchUpdateContract = dispatch(actions.actionUpdateContract);
export const dispatchInviteContract = dispatch(actions.actionInviteContract);
